import { CacheableObject } from '@nk/caching-lib';
import {
  AccountsBalanceSheetEntity,
  AccountsBalanceSheetHistoryEntity,
  AccountsBalanceSheetLatestEntity,
  AccountsHighlightsEntity,
  AccountsHighlightsHistoryEntity,
  AccountsHighlightsLatestEntity,
  AccountsIncomeStatementEntity,
  AccountsIncomeStatementHistoryEntity,
  AccountsIncomeStatementLatestEntity,
  CompanyCollateralCreditorEntity,
  CompanyCollateralTypeEntity,
  CompanyDetailsEntity,
  CompanyEntity,
  CompanyFlagHistoryAggregateEntity,
  CompanyFlagTypeEntity,
  CompanyLocationEntity,
  CompanyRiskScoreOfficialPrioritizedLatestEntity,
  CompanyShareIssueEnterpriseValueEntity,
  CompanyShareIssueEnterpriseValueHistoryEntity,
  CompanyShareIssueEnterpriseValueLatestEntity,
  GeoMunicipalityEntity,
  GeoPostalCodeEntity,
  GeoSubdivisionEntity,
  NaceCodeEntity,
  SimpleCollection,
} from '@nk/data-entities-lib';
import { includes, SortDirection, translationLookup } from '@nk/utils-lib';
import { FROM_ABBR_DICT, TO_ABBR_DICT } from './company-browser-config';

export enum TabularFileFormat {
  Csv = 'csv',
  Excel = 'xlsx',
}

export enum AccountsYearMode {
  Single = 'single',
  Multiple = 'multiple',
}

export enum CompanyBrowserFilterKey {
  NaceCodeMask = 'company_nace_code_aggregate.nace_code_mask',
  GeoCountryFieldKey = 'geo_country.country_code_two',
  GeoGeometryFieldKey = 'geo_geometry.uuid',
  SubdivisionFieldKey = 'geo_subdivision.subdivision_code',
  MunicipalityFieldKey = 'geo_municipality.municipality_code',
  AuditorCompanyUuids = 'company_affiliation_aggregate_auditors.affiliated_company_uuids',
  AuditorPersonUuids = 'company_affiliation_aggregate_auditors.affiliated_person_uuids',
  AccountantCompanyUuids = 'company_affiliation_aggregate_accountants.affiliated_company_uuids',
  AccountantPersonUuids = 'company_affiliation_aggregate_accountants.affiliated_person_uuids',
  CompanyCollateralTypeKey = 'company_collateral_type.collateral_type_key',
  CompanyCollateralCreditorUuid = 'company_collateral.company_collateral_creditor_uuid',
  CompanyCollateralCount = 'company_collateral_aggregate.collateral_count',
  CompanyCollateralAmount = 'company_collateral_aggregate.collateral_amount',
  CompanyFlagHistoryAggregateAnd = 'company_flag_history_aggregate_and.company_flag_type_uuids',
  CompanyFlagHistoryAggregateOr = 'company_flag_history_aggregate_or.company_flag_type_uuids',
  // TODO: add more
}

export function toAbbr(fieldKey: string): string {
  if (includes(fieldKey, '.')) {
    let [fieldGroup, fieldName] = fieldKey.split('.');
    fieldGroup = translationLookup(fieldGroup, TO_ABBR_DICT);
    fieldName = translationLookup(fieldName, TO_ABBR_DICT);
    return `${fieldGroup}.${fieldName}`;
  } else {
    return translationLookup(fieldKey, TO_ABBR_DICT);
  }
}

export function fromAbbr(abbreviatedFieldKey: string): string {
  if (includes(abbreviatedFieldKey, '.')) {
    let [fieldGroup, fieldName] = abbreviatedFieldKey.split('.');
    fieldGroup = translationLookup(fieldGroup, FROM_ABBR_DICT);
    fieldName = translationLookup(fieldName, FROM_ABBR_DICT);
    return `${fieldGroup}.${fieldName}`;
  } else {
    return translationLookup(abbreviatedFieldKey, FROM_ABBR_DICT);
  }
}

export class CompanyBrowserColumnKey {
  public fieldGroup: string;
  public fieldName: string;

  constructor(public fieldKey: string) {
    if (fieldKey.replace(' ', '').length < fieldKey.length) {
      throw new Error('A field key cannot contain spaces.');
    }
    const keyParts = fieldKey.split('.');
    if (keyParts.length === 2) {
      [this.fieldGroup, this.fieldName] = keyParts;
    } else {
      throw new Error('A field key must contain exactly one dot (.)');
    }
  }
}

export class CompanyBrowserColumn extends CompanyBrowserColumnKey {
  constructor(
    public fieldKey: string,
    public selectionGroup: string,
    public selectable: boolean,
    public filterable: boolean,
    public customStyles: boolean,
    public translationKey: string,
    public sortable: boolean,
    public required: boolean,
    public fieldType: string,
    public accountsFieldType: string,
  ) {
    super(fieldKey);
  }
}

export class CompanyBrowserFilter extends CompanyBrowserColumnKey implements CacheableObject {
  public values: string[];
  public value: string;

  constructor(
    fieldKey: string,
    public operator: string,
    value: string | string[],
  ) {
    super(fieldKey);
    this.values = Array.isArray(value) ? value : [value];
    if (Array.isArray(value) && value.length === 1) {
      this.value = value[0];
    } else if (Array.isArray(value)) {
      this.value = null;
    } else {
      this.value = value;
    }
  }

  public static createFromQueryString(fieldKey: string, queryString: string): CompanyBrowserFilter {
    const [operator, valueString] = queryString.split(':');
    const values = OPERATORS.In === operator || OPERATORS.NotIn === operator ? valueString.split(',') : [valueString];
    return new CompanyBrowserFilter(fieldKey, operator, values);
  }

  public toCachingKey(): string {
    return this.operator + ':' + this.values.join(',');
  }
}

export enum FilterChangeType {
  Remove = 'Remove',
  Added = 'Added',
}

export enum OPERATORS {
  // TODO: Deprecate?
  GreaterThanEquals = 'GTE',
  LessThanEquals = 'LTE',
  GreaterThan = 'GT',
  LessThan = 'LT',
  Equals = 'EQ',
  In = 'IN',
  NotIn = 'NOT_IN',
  Similar = 'ILIKE',
  Search = 'SEARCH',
}

export const OPERATOR_TRANSLATION_KEY = {
  [OPERATORS.GreaterThanEquals]: 'greater_than_equals',
  [OPERATORS.LessThanEquals]: 'less_than_equals',
  [OPERATORS.GreaterThan]: 'greater_than',
  [OPERATORS.LessThan]: 'less_than',
  [OPERATORS.Equals]: 'is',
  [OPERATORS.In]: 'is_one_of',
  [OPERATORS.NotIn]: 'is_not',
  [OPERATORS.Similar]: 'contains_text',
  [OPERATORS.Search]: 'is_similar_to',
};

export enum CompanyBrowserFilterOperator {
  GreaterThanEquals = 'GTE',
  LessThanEquals = 'LTE',
  GreaterThan = 'GT',
  LessThan = 'LT',
  Equals = 'EQ',
  In = 'IN',
  NotIn = 'NOT_IN',
  Similar = 'ILIKE',
  Search = 'SEARCH',
}

export class CompanyCollateralAggregate {
  constructor(
    public collateral_amount: number,
    public collateral_count: number,
    public company_collateral_creditor_uuids: string[],
    public company_collateral_type_uuids: string[],
    public company_collateral_creditors: CompanyCollateralCreditorEntity[],
    public company_uuid: string,
    public uuid: string,
  ) {}
}

export class CompanyNaceCodeAggregate {
  constructor(
    public company_uuid: string,
    public nace_code_uuids: string[],
    public nace_codes: string[],
  ) {}
}

export class WatchlistMappingAggregate {
  constructor(
    public uuid: string,
    public company_uuid: string,
    public watchlist_uuids: string[],
  ) {}
}

export class MentionSearchAggregate {
  constructor(
    public uuid: string,
    public company_uuid: string,
    public mention_search_count: number,
    public mention_search_target: string,
    public app_url: string,
  ) {}
}

export class CompanyBrowserOrdering extends CompanyBrowserColumnKey {
  constructor(
    fieldKey: string,
    public orderDirection: SortDirection,
  ) {
    super(fieldKey);
  }

  public static createFromQueryString(orderingString: string): CompanyBrowserOrdering {
    const [abbrFieldKey, orderDirection] = orderingString.split(':');
    return new CompanyBrowserOrdering(fromAbbr(abbrFieldKey), orderDirection as SortDirection);
  }
}

export interface CompanyBrowserExtendedCompositeEntity extends CompanyBrowserCompositeEntity {
  company_flag_types: CompanyFlagTypeEntity[];
  nace_codes: NaceCodeEntity[];
  municipality: GeoMunicipalityEntity;
  subdivision: GeoSubdivisionEntity;
  postal_code: GeoPostalCodeEntity;
  collateral_types: CompanyCollateralTypeEntity[];
  collateral_creditors: CompanyCollateralCreditorEntity[];
}

export interface CompanyBrowserExtendedCompositeCollection
  extends SimpleCollection<CompanyBrowserExtendedCompositeEntity> {
  query: string;
  query_duration: number;
  query_success: boolean;
}

export class CompanyBrowserCompositeEntity {
  constructor(
    public company: CompanyEntity,
    public company_details: CompanyDetailsEntity,
    public company_location: CompanyLocationEntity,
    public accounts_highlights: AccountsHighlightsEntity,
    public accounts_income_statement: AccountsIncomeStatementEntity,
    public accounts_balance_sheet: AccountsBalanceSheetEntity,
    public company_share_issue_enterprise_value: CompanyShareIssueEnterpriseValueEntity,
    public accounts_highlights_latest: AccountsHighlightsLatestEntity,
    public accounts_income_statement_latest: AccountsIncomeStatementLatestEntity,
    public accounts_balance_sheet_latest: AccountsBalanceSheetLatestEntity,
    public company_share_issue_enterprise_value_latest: CompanyShareIssueEnterpriseValueLatestEntity,
    public accounts_highlights_history: AccountsHighlightsHistoryEntity,
    public accounts_income_statement_history: AccountsIncomeStatementHistoryEntity,
    public accounts_balance_sheet_history: AccountsBalanceSheetHistoryEntity,
    public company_share_issue_enterprise_value_history: CompanyShareIssueEnterpriseValueHistoryEntity,
    public company_risk_score_official_prioritized_latest: CompanyRiskScoreOfficialPrioritizedLatestEntity,
    public company_flag_history_aggregate: CompanyFlagHistoryAggregateEntity,
    public company_nace_code_aggregate: CompanyNaceCodeAggregate,
    public company_collateral_aggregate: CompanyCollateralAggregate,
    public watchlist_mapping_aggregate: WatchlistMappingAggregate,
    public mention_search_aggregate: MentionSearchAggregate,
  ) {}
}

export interface BrowserFilterChange {
  browserFilter: CompanyBrowserFilter;
  filterChangeType: FilterChangeType;
}
